import { ExpressionVisitor } from 'app/model/adapters/dataflow/dslExpressions/expression.visitor';
import {
    Expression, IntegerLiteral, FloatLiteral, StringLiteral, FunctionExpression, RegexLiteral
} from 'app/model/dataflow/expressionTypes';
import { DataFlowColumnArgument } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowExpressionFunctions } from 'app/model/dataflow/dataflow-expression-functions';
import { StringDataFlowType } from '../../../../model/dataflow/dataflow-datatype';
import { DataFlowFunctionArgument } from 'app/model/dataflow/dataflow-function-argument';
import { IMarkerData } from 'app/shared/components/monaco/monaco-models';

/* eslint-disable @typescript-eslint/no-explicit-any */

export class ModelEvaluatorVisitor extends ExpressionVisitor {
    public modelMarkers: IMarkerData[] = [];
    public usedColumns: DataFlowColumnArgument[] = [];

    constructor(tokenStream: any, public dataflowRuntimeContext: Map<string, any>) {
        super(tokenStream);
    }

    public visitColumnOperandRef(context: any): Expression {
        const expression = super.visitColumnOperandRef(context);

        let typeValue: string;

        if (!!this.dataflowRuntimeContext.get(expression.columnOperand.name.basicName.identifier.name)) {
            typeValue = this.dataflowRuntimeContext.get(expression.columnOperand.name.basicName.identifier.name);
        }

        if (typeValue) {
            expression.returnType = new StringDataFlowType();
            expression.returnValue = typeValue;
        }

        return expression;
    }

    public visitOr(context: any): Expression {
        const expression = super.visitOr(context);
        if (expression.or.leftExpression && expression.or.rightExpression) {
            expression.returnValue = expression.or.leftExpression.returnValue || expression.or.rightExpression.returnValue;
        }

        return expression;
    }

    public visitFunctionExpr(context: any): Expression {
        const expression = super.visitFunctionExpr(context);
        if (expression.functionExpression.basicName.identifier) {
            const availableFunction = DataFlowExpressionFunctions.ALL_INLCUDING_PATTERN_FUNCTIONS.find((dfFunction) => dfFunction.name === expression.functionExpression.basicName.identifier.name);
            if (availableFunction) {
                const dataflowFunction: DataFlowFunctionArgument = availableFunction as DataFlowFunctionArgument;
                expression.returnValue = dataflowFunction.evaluate(...(expression.value as FunctionExpression).children);
                expression.functionExpression.isCompileTimeExecutable = dataflowFunction.isExecutable();
            }
        }
        return expression;
    }

    public visitAnd(context: any): Expression {
        const expression = super.visitAnd(context);

        if (expression.and.leftExpression && expression.and.rightExpression) {
            expression.returnValue = expression.and.leftExpression.returnValue && expression.and.rightExpression.returnValue;
        }

        return expression;
    }

    public visitAddMinus(context: any): Expression {
        const expression = super.visitAddMinus(context);

        if (expression.addMinus.operation === 'plus') {
            if (expression.addMinus.leftExpression && expression.addMinus.rightExpression) {
                expression.returnValue = expression.addMinus.leftExpression.returnValue + expression.addMinus.rightExpression.returnValue;
            }
        } else {
            if (expression.addMinus.leftExpression && expression.addMinus.rightExpression) {
                expression.returnValue = expression.addMinus.leftExpression.returnValue - expression.addMinus.rightExpression.returnValue;
            }
        }
        return expression;
    }

    public visitLiteralExpr(context: any): Expression {
        const expression = super.visitLiteralExpr(context);
        if (expression.literal.value instanceof IntegerLiteral || expression.literal.value instanceof FloatLiteral) {
            expression.returnValue = +expression.literal.value.value.value;
        } else if (expression.literal.value instanceof StringLiteral || expression.literal.value instanceof RegexLiteral) {
            expression.returnValue = expression.literal.value.value.substring(1, expression.literal.value.value.length - 1);
        }

        return expression;
    }

    public visitInterpolation(context: any): Expression {
        const expression = super.visitInterpolation(context) as any;
        if (expression.value && expression.value.value && expression.value.expressions) {
            let returnValue = expression.value.value as string;
            expression.value.expressions.forEach((interpolationExpression: any) => {
                const visitedExpression = this.visit(interpolationExpression);
                returnValue = returnValue.replace(`{${interpolationExpression.getText()}}`, visitedExpression.returnValue);
            });
            expression.returnValue = returnValue.substr(1, returnValue.length - 2);
        }
        return expression;
    }

    public visitMetaVariableRef(context: any): Expression {
        const expression = super.visitMetaVariableRef(context);

        if (!!this.dataflowRuntimeContext.get(expression.metaVariable.name)) {
            expression.returnValue = this.dataflowRuntimeContext.get(expression.metaVariable.name);
        }

        return expression;
    }

    public visitMetaPosRef(context: any): Expression {
        const expression = super.visitMetaPosRef(context);

        if (!!this.dataflowRuntimeContext.get(expression.metaVariable.name)) {
            expression.returnValue = this.dataflowRuntimeContext.get(expression.metaVariable.name);
        }

        return expression;
    }

    public visitMetaMatchRef(context: any): Expression {
        const expression = super.visitMetaMatchRef(context);
        expression.returnValue = this.dataflowRuntimeContext.get(expression.metaMatch.name) || '';
        return expression;
    }

    public visitComparison(context: any): Expression {
        const expression = super.visitComparison(context);

        if (expression.comparison.leftExpression && expression.comparison.rightExpression) {
            switch (expression.comparison.operation) {
                case 'gt':
                    expression.returnValue = expression.comparison.leftExpression.returnValue > expression.comparison.rightExpression.returnValue;
                    break;
                case 'gtEq':
                    expression.returnValue = expression.comparison.leftExpression.returnValue >= expression.comparison.rightExpression.returnValue;
                    break;
                case 'lt':
                    expression.returnValue = expression.comparison.leftExpression.returnValue < expression.comparison.rightExpression.returnValue;
                    break;
                case 'ltEq':
                    expression.returnValue = expression.comparison.leftExpression.returnValue <= expression.comparison.rightExpression.returnValue;
                    break;
                default:
                    break;
            }

        }
        return expression;
    }

    public visitScope(context: any): Expression {
        const expression = super.visitScope(context);
        expression.returnValue = expression.scope.expression.returnValue;
        return expression;
    }

    public visitEqNotEq(context: any): Expression {
        const expression = super.visitEqNotEq(context);

        if (expression.eqNotEq.leftExpression && expression.eqNotEq.rightExpression) {
            expression.returnValue = expression.eqNotEq.operation === 'eq'
                ? expression.eqNotEq.leftExpression.returnValue === expression.eqNotEq.rightExpression.returnValue
                : expression.eqNotEq.leftExpression.returnValue !== expression.eqNotEq.rightExpression.returnValue;
        }

        return expression;
    }

    public visitUnaryMinus(context: any): Expression {
        const expression = super.visitUnaryMinus(context);
        if (expression.unaryMinus.expression) {
            expression.returnValue = -expression.unaryMinus.expression.returnValue;
        }
        return expression;
    }

    public visitNot(context: any): Expression {
        const expression = super.visitNot(context);
        if (expression.not.expression) {
            expression.returnValue = !expression.not.expression.returnValue;
        }
        return expression;
    }
}
